// Faturamento / Limite do MEI — "quanto já faturei, quanto posso faturar, vou estourar?"
// Sem divisão pro-cliente/minha-cópia (controle interno). Aposta nº 1 de SEO.
// docs/calculamei-spec-calculadoras.md — seção 4.
// Teto por ano: 2026 R$ 81 mil · 2027 R$ 110 mil · 2028+ R$ 140 mil (LC 238/2026).

import {
  LIMITE_MEI_ANO_REFERENCIA,
  TOLERANCIA_EXCESSO_MEI,
  getLimiteMei,
  getLimiteMeiMes,
} from './constantes'

export type FaturamentoModo = 'projecao' | 'controle'
export type FaturamentoStatus = 'tranquilo' | 'atencao' | 'estourou'

/**
 * Onde o valor do ano cai em relação ao teto (LC 123, art. 18-A, § 7º, III):
 * - dentro: até o teto;
 * - ate20: passou em até 20% — desenquadra a partir de janeiro do ano seguinte;
 * - acima20: passou mais de 20% — desenquadramento retroativo a janeiro do ano.
 */
export type FaturamentoFaixaExcesso = 'dentro' | 'ate20' | 'acima20'

export interface FaturamentoMesItem {
  /** Rótulo do mês (1–12 ou nome) — opcional, só pra exibição. */
  mes?: number | string
  valor: number
}

export interface FaturamentoInputs {
  modo: FaturamentoModo
  /** Ano-calendário simulado (define o teto). Padrão: ano corrente. */
  ano?: number
  /** Projeção: média mensal estimada. */
  faturamentoMes?: number
  /** Controle: o que já entrou no ano (aceita meses parciais). */
  meses?: FaturamentoMesItem[]
}

export interface FaturamentoResultado {
  modo: FaturamentoModo
  limiteAno: number
  limiteMes: number
  anoReferencia: number
  /** Teto + 20%: até aqui o desenquadramento só vale no ano seguinte. */
  limiteTolerancia: number
  /** Projeção → projeção anual; Controle → total já faturado no ano. */
  valorAno: number
  /** % do teto já usado (pode passar de 100). */
  percentUsado: number
  /** Quanto ainda cabe até o teto (negativo se estourou). */
  restanteAno: number
  /** Controle: média mensal dos meses preenchidos (null na projeção). */
  mediaMensal: number | null
  /** Controle: projeção de fim de ano pela média (null na projeção). */
  projecaoFim: number | null
  /** Controle: quantos meses foram preenchidos (null na projeção). */
  mesesPreenchidos: number | null
  status: FaturamentoStatus
  /** Faixa de excesso do valor considerado (projeção do ano ou projeção de fim de ano). */
  faixaExcesso: FaturamentoFaixaExcesso
}

const round2 = (n: number) => Number(n.toFixed(2))

export function faixaExcessoMei(valorAno: number, limiteAno: number): FaturamentoFaixaExcesso {
  if (valorAno <= limiteAno) return 'dentro'
  if (valorAno <= limiteAno * (1 + TOLERANCIA_EXCESSO_MEI)) return 'ate20'
  return 'acima20'
}

export function calcularFaturamento(inputs: FaturamentoInputs): FaturamentoResultado {
  const { modo } = inputs
  const ano = inputs.ano ?? LIMITE_MEI_ANO_REFERENCIA
  const limiteAno = getLimiteMei(ano)
  const limiteMes = getLimiteMeiMes(ano)

  let valorAno = 0
  let mediaMensal: number | null = null
  let projecaoFim: number | null = null
  let mesesPreenchidos: number | null = null

  if (modo === 'projecao') {
    valorAno = (inputs.faturamentoMes || 0) * 12
  } else {
    const meses = inputs.meses || []
    const preenchidos = meses.filter((m) => (m.valor || 0) > 0)
    valorAno = meses.reduce((s, m) => s + (m.valor || 0), 0)
    mesesPreenchidos = preenchidos.length
    mediaMensal = preenchidos.length > 0 ? valorAno / preenchidos.length : 0
    projecaoFim = mediaMensal * 12
  }

  const percentUsado = (valorAno / limiteAno) * 100
  const restanteAno = limiteAno - valorAno

  let status: FaturamentoStatus
  if (modo === 'projecao') {
    status = valorAno > limiteAno ? 'estourou' : percentUsado > 80 ? 'atencao' : 'tranquilo'
  } else {
    // Menos de ~2 meses de teto sobrando = atenção.
    status = valorAno > limiteAno ? 'estourou' : restanteAno / limiteMes < 2 ? 'atencao' : 'tranquilo'
  }

  // No acompanhamento, a faixa olha pra onde o ano vai terminar (o pior entre o já faturado e a projeção).
  const valorFaixa = modo === 'projecao' ? valorAno : Math.max(valorAno, projecaoFim ?? 0)

  return {
    modo,
    limiteAno,
    limiteMes,
    anoReferencia: ano,
    limiteTolerancia: round2(limiteAno * (1 + TOLERANCIA_EXCESSO_MEI)),
    valorAno: round2(valorAno),
    percentUsado: round2(percentUsado),
    restanteAno: round2(restanteAno),
    mediaMensal: mediaMensal === null ? null : round2(mediaMensal),
    projecaoFim: projecaoFim === null ? null : round2(projecaoFim),
    mesesPreenchidos,
    status,
    faixaExcesso: faixaExcessoMei(valorFaixa, limiteAno),
  }
}
